'use client'

/**
 * Client-side inactivity timeout.
 *
 * Tracks user activity (mouse / keyboard / touch / scroll) and:
 *   - opens a warning at `warningAfterMs` (default 7h55m) idle
 *   - calls `signOut()` and redirects to `/login?reason=inactivity`
 *     at `logoutAfterMs` (default 8h) idle
 *
 * Better-Auth's server-side session expiry (30 days) is not affected;
 * this is a UX backstop to drop a genuinely-idle foreground session
 * before the long server TTL — not an aggressive short timeout.
 *
 * Activity events are debounced to ~1s to keep the listeners cheap.
 * Background tabs do NOT accrue idle time: on `visibilitychange` to
 * hidden the timers are paused, and on return to the tab the idle
 * window resets to a fresh full duration. This matches how large
 * consumer / professional networks (LinkedIn, etc.) behave — long-lived
 * sessions with no surprise logout of a tab you left open in the
 * background — while the active-tab cap still drops a truly idle
 * foreground session before the 30-day server TTL. (Historically the
 * timer kept running while hidden, at a 20/25 min cap; 2026-08-09 QA
 * found that logged people — and background tabs — out far too often.)
 *
 * Cross-tab coordination: every tab broadcasts activity timestamps on
 * a BroadcastChannel. A tab that has been idle but receives an activity
 * ping from a sibling resets its timers (and dismisses any showing
 * warning) - this prevents the multi-tab race where tab A is active
 * but tab B forces a sign-out behind the user's back. Inactivity-driven
 * sign-outs also broadcast so every tab redirects with the same reason.
 */

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { signOut } from './client'

/** Default: warn 5 min before the hard cap (i.e. at 7h55m idle). */
export const INACTIVITY_WARNING_MS = (8 * 60 - 5) * 60 * 1000
/** Default: hard sign-out at 8h idle on the active tab (background time is not counted). */
export const INACTIVITY_LOGOUT_MS = 8 * 60 * 60 * 1000
/** Activity-event coalescing window. */
const ACTIVITY_DEBOUNCE_MS = 1000
/** BroadcastChannel name for cross-tab activity sync. */
const INACTIVITY_CHANNEL = 'pv-inactivity-sync'

interface Options {
  /** Override warning threshold in ms. Default 7h55m (INACTIVITY_WARNING_MS). */
  warningAfterMs?: number
  /** Override hard-logout threshold in ms. Default 8h (INACTIVITY_LOGOUT_MS). */
  logoutAfterMs?: number
  /** Skip the hook entirely (e.g. when no user is signed in). */
  enabled?: boolean
}

interface InactivityState {
  /** Whether the warning modal should be shown. */
  showWarning: boolean
  /** ms remaining until forced sign-out, derived from logoutAfterMs - elapsed. */
  remainingMs: number
  /** Dismiss the warning and reset the idle timer. */
  stayActive: () => void
  /** Sign out immediately. */
  signOutNow: () => void
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'touchstart',
  'scroll',
] as const

type SyncMessage =
  | { kind: 'activity'; t: number }
  | { kind: 'signed_out' }

export function useInactivityTimeout(options: Options = {}): InactivityState {
  const {
    warningAfterMs = INACTIVITY_WARNING_MS,
    logoutAfterMs = INACTIVITY_LOGOUT_MS,
    enabled = true,
  } = options

  const router = useRouter()
  const [showWarning, setShowWarning] = useState(false)
  const [remainingMs, setRemainingMs] = useState(
    Math.max(0, logoutAfterMs - warningAfterMs),
  )

  // Mirror showWarning into a ref so the activity listener (hot path)
  // reads the current value without forcing a listener teardown each
  // time the warning state flips.
  const showWarningRef = useRef(false)
  showWarningRef.current = showWarning

  // Last activity timestamp. Stored in a ref so activity listeners
  // never trigger re-renders (they'd be hot path on `mousemove`).
  const lastActivityRef = useRef<number>(Date.now())
  const lastDebounceRef = useRef<number>(0)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const signedOutRef = useRef(false)
  const bcRef = useRef<BroadcastChannel | null>(null)
  // Lets stayActive() trigger a fresh schedule without going through
  // the effect (we removed showWarning from the dep list).
  const scheduleTimersRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!enabled) return

    try {
      bcRef.current = new BroadcastChannel(INACTIVITY_CHANNEL)
    } catch {
      bcRef.current = null
    }

    function broadcast(msg: SyncMessage) {
      try {
        bcRef.current?.postMessage(msg)
      } catch {
        // BroadcastChannel may be closed mid-flight; ignore.
      }
    }

    function clearTimers() {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
      warningTimerRef.current = null
      logoutTimerRef.current = null
      countdownTimerRef.current = null
    }

    async function performSignOut() {
      if (signedOutRef.current) return
      signedOutRef.current = true
      clearTimers()
      broadcast({ kind: 'signed_out' })
      try {
        await signOut()
      } catch {
        // Even if signOut fails (network blip), still redirect; the
        // server will reject the cookie on the next request anyway.
      }
      router.push('/login?reason=inactivity')
    }

    function scheduleTimers() {
      clearTimers()
      const now = Date.now()
      const elapsed = now - lastActivityRef.current
      const warnIn = Math.max(0, warningAfterMs - elapsed)
      const logoutIn = Math.max(0, logoutAfterMs - elapsed)

      if (logoutIn === 0) {
        void performSignOut()
        return
      }

      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true)
        // Drive the modal countdown.
        countdownTimerRef.current = setInterval(() => {
          const left = Math.max(
            0,
            logoutAfterMs - (Date.now() - lastActivityRef.current),
          )
          setRemainingMs(left)
          if (left === 0 && countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current)
            countdownTimerRef.current = null
          }
        }, 1000)
        setRemainingMs(
          Math.max(
            0,
            logoutAfterMs - (Date.now() - lastActivityRef.current),
          ),
        )
      }, warnIn)

      logoutTimerRef.current = setTimeout(performSignOut, logoutIn)
    }
    scheduleTimersRef.current = scheduleTimers

    function recordActivity() {
      const now = Date.now()
      // Don't reset the timer once the warning is showing - the user
      // explicitly has to click "Stay signed in" to dismiss it. This
      // matches the spec ("two buttons … resets timer").
      if (showWarningRef.current) return
      if (now - lastDebounceRef.current < ACTIVITY_DEBOUNCE_MS) return
      lastDebounceRef.current = now
      lastActivityRef.current = now
      scheduleTimers()
      broadcast({ kind: 'activity', t: now })
    }

    if (bcRef.current) {
      bcRef.current.onmessage = (event: MessageEvent<SyncMessage>) => {
        const data = event.data
        if (!data || typeof data !== 'object') return

        if (data.kind === 'activity' && typeof data.t === 'number') {
          // A sibling tab saw activity. Adopt its timestamp if newer
          // and reschedule. If our warning was open, dismiss it - the
          // user is interacting elsewhere, so they're still present.
          if (data.t > lastActivityRef.current) {
            lastActivityRef.current = data.t
            lastDebounceRef.current = data.t
            if (showWarningRef.current) {
              setShowWarning(false)
              setRemainingMs(Math.max(0, logoutAfterMs - warningAfterMs))
            }
            scheduleTimers()
          }
          return
        }

        if (data.kind === 'signed_out') {
          // A sibling tab signed out due to inactivity. Mirror the
          // redirect locally without re-broadcasting (avoids loops).
          if (signedOutRef.current) return
          signedOutRef.current = true
          clearTimers()
          router.push('/login?reason=inactivity')
        }
      }
    }

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, recordActivity, { passive: true })
    }

    // Pause the idle timer while the tab is hidden so a backgrounded tab
    // never accrues idle time and never gets signed out behind the user's
    // back; resume with a fresh full window when the tab becomes visible.
    function handleVisibility() {
      if (document.hidden) {
        clearTimers()
        return
      }
      lastActivityRef.current = Date.now()
      lastDebounceRef.current = Date.now()
      if (showWarningRef.current) {
        setShowWarning(false)
        setRemainingMs(Math.max(0, logoutAfterMs - warningAfterMs))
      }
      scheduleTimers()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // Initial scheduling on mount.
    lastActivityRef.current = Date.now()
    scheduleTimers()

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, recordActivity)
      }
      document.removeEventListener('visibilitychange', handleVisibility)
      clearTimers()
      bcRef.current?.close()
      bcRef.current = null
      scheduleTimersRef.current = () => {}
    }
  }, [enabled, warningAfterMs, logoutAfterMs, router])

  function stayActive() {
    const now = Date.now()
    lastActivityRef.current = now
    lastDebounceRef.current = now
    setShowWarning(false)
    setRemainingMs(Math.max(0, logoutAfterMs - warningAfterMs))
    // Tell other tabs to dismiss their warnings + reschedule.
    try {
      bcRef.current?.postMessage({ kind: 'activity', t: now } satisfies SyncMessage)
    } catch {
      // ignore
    }
    // Re-arm timers immediately from the fresh timestamp.
    scheduleTimersRef.current()
  }

  function signOutNow() {
    if (signedOutRef.current) return
    signedOutRef.current = true
    try {
      bcRef.current?.postMessage({ kind: 'signed_out' } satisfies SyncMessage)
    } catch {
      // ignore
    }
    void (async () => {
      try {
        await signOut()
      } catch {
        // Same rationale as above - fall through to the redirect.
      }
      router.push('/login')
    })()
  }

  return { showWarning, remainingMs, stayActive, signOutNow }
}
